import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/base/AdvanceTable';
import Badge, { BadgeBg } from 'components/base/Badge';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Col, Dropdown, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CommissionDetailsModal, { ProductCommissionDetails, CommissionCalculation } from 'components/modals/CommissionDetailsModal';
import { useNavigate, Link } from 'react-router-dom';
import { faPlus, faList, faClock } from '@fortawesome/free-solid-svg-icons';
import { ColumnDef } from '@tanstack/react-table';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { SELLER_PRODUCTS_QUERY } from 'api/graphql/queries/GetProductList';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { currencyFormat } from 'helpers/utils';

// Import the ShippingMethod enum from our shared component
import { ShippingMethod } from 'components/modals/CommissionDetailsModal';

// Define the product type based on what we need
type BestSellingProductType = {
  id: string;
  name: string;
  sku: string;
  price: number;
  status: number;
  productStatus: number;
  productImage: string;
  totalSold: number;
  type: string;
  sellerReference?: string;
  visibility?: number;
  // Commission-related fields
  dimensions?: {
    longueur?: number;
    largeur?: number;
    hauteur?: number;
  };
  weight?: number | string;
  percentCommission?: number | string;
  fixCommission?: number | string;
  specialPrice?: number | string | null;
  discountStartDate?: string | null;
  discountEndDate?: string | null;
  shippingMethod?: number | string;
  category?: any[]; // Category data
};

// Define the API product type
type ApiProduct = {
  entityid: string;
  name: string;
  sku: string;
  prices: number;
  status: number;
  product_status: number;
  image1?: { url?: string };
  type_id: string;
  total_sold?: number;
  seller_reference?: string;
  visibility?: number;
  longueur25?: string | number;
  dimensions_de_l_outil_largeur1?: string | number;
  dimensions_de_l_outil_hauteur1?: string | number;
  percent_commission_for_product?: string | number;
  commission_for_product?: string | number;
  specialprice?: string | number;
  start_date?: string;
  end_date?: string;
  mode_expedition?: string | number;
  weight?: string | number;
  sellercategories?: any[]; // Category data from API
};

// Create context for sharing the callback functions
export const BestSellingTableContext = createContext<{
  onShowCommissionDetails?: (product: BestSellingProductType) => void;
} | null>(null);

// Define the columns for our table
const createColumns = (t: any): ColumnDef<BestSellingProductType>[] => [
  {
    id: "product",
    accessorKey: "product",
    header: t("productDetails"),
    cell: ({ row: { original } }) => {
      const { name, productImage, sku, sellerReference, type, visibility } = original;
      const navigate = useNavigate();
      return (
        <div className="d-flex align-items-center">
          {/* Product Image */}
          <img
            src={productImage}
            alt={name}
            width={53}
            height={53}
            className="me-3 border border-translucent rounded-2"
            style={{ objectFit: "contain" }}
          />

          {/* Product Information */}
          <div className="d-flex flex-column">
            {/* Product Name */}
            <span 
              className="fw-semibold line-clamp-3 text-body-highlight cursor-pointer" 
              onClick={() => navigate(`/products/${original.id}`)}
              style={{ textDecoration: 'underline' }}
            >
              {name}
            </span>

            {/* SKU */}
              <span className="text-body-secondary fs-10">SKU: {sku}</span>

            {/* Seller Reference */}
            <span className="text-body-highlight fs-10">
              {t("sellerReference")}: {sellerReference || t("notAvailable")}
            </span>
          </div>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: 350 }, className: "ps-4" },
      cellProps: { className: "ps-4" },
    },
  },
  {
    id: "type",
    accessorKey: "type",
    header: t("type"),
    cell: ({ row: { original } }) => {
      const { type, visibility } = original;

      // Determine badge variant and display text based on type and visibility
      let variant: BadgeBg;
      let displayText: string;

      if (type === "configurable") {
        variant = "primary";
        displayText = t("configurable");
      } else if (type === "simple" && visibility === 1) {
        variant = "info"; // Use a distinct color for Variant
        displayText = t("variant");
      } else {
        variant = "secondary";
        displayText = t("simple");
      }

      return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
    },
    meta: {
      headerProps: { style: { width: 200 }, className: "text-center ps-4" },
      cellProps: { className: "ps-4 text-center" },
    },
  },
  {
    id: "situation",
    accessorKey: "status",
    header: t("situation"),
    cell: ({ row: { original } }) => {
      const { status } = original;
      const displayText = status === 1 ? t("onSale") : t("closeForSale");
      const variant: BadgeBg = status === 1 ? "success" : "danger";

      return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
    },
    meta: {
      headerProps: { style: { width: 120 }, className: "text-center ps-4" },
      cellProps: { className: "ps-4 text-center" },
    },
  },
  {
    accessorKey: 'totalSold',
    header: t('totalSold'),
    cell: ({ row: { original } }) => {
      const { totalSold } = original;
      
      // Determine badge variant based on total sold count
      const getBadgeVariant = (count: number): BadgeBg => {
        if (count >= 100) return "danger";    // Red for top sellers
        if (count >= 50) return "warning";    // Orange/Yellow for high sellers
        if (count >= 30) return "success";    // Green for good sellers
        if (count >= 10) return "info";       // Blue for moderate sellers
        return "secondary";                   // Gray for new sellers
      };
      
      // Get an appropriate icon based on the total sold count
      const getIcon = (count: number): string => {
        if (count >= 100) return "🔥"; // Fire for top sellers
        if (count >= 50) return "✨"; // Sparkles for high sellers
        if (count >= 30) return "⭐"; // Star for good performers
        if (count >= 10) return "📈"; // Chart for moderate performers
        return "🙌"; // Dizzy symbol for new products
      };
      
      return (
        <Badge 
          bg={getBadgeVariant(totalSold)} 
          variant="phoenix"
          style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}
        >
          {getIcon(totalSold)} {totalSold}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: 150 }, className: "text-center ps-4" },
      cellProps: { className: "ps-4 text-center" },
    },
  },
  {
    id: "price",
    accessorKey: "price",
    header: t("price"),
    cell: ({ row: { original } }) => {
      // Get the global table context
      const context = useContext(BestSellingTableContext);
      // Extract the onShowCommissionDetails function from the context
      const onShowCommissionDetails = context?.onShowCommissionDetails;

      return (
        <span 
          className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
          onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
        >
          {currencyFormat(original.price, { currency: "TND" })}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: 150 }, className: "ps-4 text-center" },
      cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center" },
    },
  },
  {
    id: "shippingMethod",
    accessorKey: "shippingMethod",
    header: t("shippingMethod"),
    cell: ({ row: { original } }) => {
      const { shippingMethod } = original;
      let displayText = "";
      let variant: BadgeBg = "secondary";
      if (Number(shippingMethod) === ShippingMethod.FBW) {
        displayText = t("shippingMethodType.fbw");
        variant = "success";
      } else if (Number(shippingMethod) === ShippingMethod.ESS) {
        displayText = t("shippingMethodType.ess");
        variant = "warning";
      } else if (Number(shippingMethod) === ShippingMethod.ESW) {
        displayText = t("shippingMethodType.esw");
        variant = "info";
      }
      return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
    },
    meta: {
      headerProps: { style: { width: 200 }, className: "ps-4" },
      cellProps: { className: "fs-9 fw-semibold ps-4 text-body-highlight text-opacity-85" },
    },
  },
  {
    id: "category",
    accessorKey: "category",
    header: t("category"),
    meta: {
      headerProps: { style: { width: 150 }, className: "ps-4" },
      cellProps: { className: "ps-4 text-center" },
    },
    cell: ({ row: { original } }) => {
      const { category } = original;

      if (!category || category.length === 0) {
        return t("notAvailable");
      }

      return (
        <div className="d-flex flex-wrap gap-1">
          {category.map((cat: any, index: number) => (
            <Badge key={index} bg="secondary" variant="phoenix">
              {cat.name}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: "product_status",
    accessorKey: "productStatus",
    header: t("approvalStatus"),
    cell: ({ row: { original } }) => {
      const { productStatus } = original;

      const displayText =
        productStatus === 1 ? t("approved") : productStatus === 3 ? t("rejected") : t("disapproved");
      const variant: BadgeBg =
        productStatus === 1 ? "success" : productStatus === 3 ? "danger" : "warning";

      return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
    },
    meta: {
      headerProps: { className: 'text-end' },
      cellProps: { className: 'text-end pe-5' }
    }
  },
  
];

// Best selling products table component
const EcomBestSellingProductsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<BestSellingProductType[]>([]);
  
  // Time period filter state
  const [timePeriod, setTimePeriod] = useState<string>('week'); // Default to 'week'
  const [timePeriodLabel, setTimePeriodLabel] = useState(t('thisWeek'));
  
  // Modal state for commission details
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [currentCommissionDetails, setCurrentCommissionDetails] = useState<BestSellingProductType | null>(null);

  // Handler for time period changes
  const handleTimePeriodChange = (period: string | null) => {
    // Set to empty string for 'All Time' to remove filter, otherwise use the period
    setTimePeriod(period === null ? '' : period);
    
    // Update the label based on selected period
    if (period === 'week') {
      setTimePeriodLabel(t('thisWeek'));
    } else if (period === 'month') {
      setTimePeriodLabel(t('lastMonth'));
    } else if (period === 'three_months') {
      setTimePeriodLabel(t('lastThreeMonths'));
    } else {
      setTimePeriodLabel(t('allTime'));
    }
  };

  // Fetch best-selling products
  const { data, loading, error } = useQuery(SELLER_PRODUCTS_QUERY, {
    variables: {
      page: 1,
      pageSize: 10, // Show more products since we don't have pagination
      filter: { 
        best_sells: true,
        type_id: "simple",  // Only get simple products
        time_period: timePeriod,  // Add time period filter
      }
    },
    fetchPolicy: 'network-only'
  });

  // Transform the data for our table
  useEffect(() => {
    if (data?.sellerProducts?.products) {
      // Filter out products with totalSold = 0 (client-side filter until backend is updated)
      const filteredProducts = data.sellerProducts.products.filter(
        (product: ApiProduct) => product.total_sold && product.total_sold > 0
      );
      
      const transformedData = filteredProducts.map((product: ApiProduct) => ({
        id: product.entityid || '',
        name: product.name || 'Unnamed Product',
        sku: product.sku || '',
        price: product.prices || 0,
        status: product.status || 0,
        productStatus: product.product_status || 0,
        productImage: product.image1?.url || '',
        totalSold: product.total_sold || 0,
        type: product.type_id || '',
        sellerReference: product.seller_reference || '',
        visibility: product.visibility || 0,
        // Store additional fields for commission calculation
        dimensions: {
          longueur: product.longueur25 || 0,
          largeur: product.dimensions_de_l_outil_largeur1 || 0,
          hauteur: product.dimensions_de_l_outil_hauteur1 || 0
        },
        weight: product.weight || 0,
        percentCommission: product.percent_commission_for_product || 0,
        fixCommission: product.commission_for_product || 0,
        specialPrice: product.specialprice || null,
        discountStartDate: product.start_date || null,
        discountEndDate: product.end_date || null,
        shippingMethod: product.mode_expedition || 1,
        // Map category data from sellercategories
        category: product.sellercategories || []
      }));
      setTableData(transformedData);
    }
  }, [data]);

  // Calculate the total commission
  const calculateTotalCommission = (product: ProductCommissionDetails): CommissionCalculation => {
    // Get the real commission values from the original product (from the currentCommissionDetails)
    const originalProduct = currentCommissionDetails;
    
    // Get percent commission (default to 5% if not available)
    const percentCommission = originalProduct?.percentCommission 
      ? parseFloat(String(originalProduct.percentCommission)) 
      : 5;
    
    // Get fix commission (default to 1.0 if not available)
    const fixCommission = originalProduct?.fixCommission 
      ? parseFloat(String(originalProduct.fixCommission)) 
      : 1.0;
    
    // Check if special price is applicable (based on dates)
    const now = new Date();
    const startDate = originalProduct?.discountStartDate ? new Date(originalProduct.discountStartDate) : null;
    const endDate = originalProduct?.discountEndDate ? new Date(originalProduct.discountEndDate) : null;
    
    const hasValidDates = startDate !== null && endDate !== null;
    const isWithinDateRange = hasValidDates ? (startDate <= now && now <= endDate) : false;
    const hasSpecialPrice = product.specialPrice !== null && product.specialPrice !== undefined && Number(product.specialPrice) > 0;
    const isSpecialPriceApplicable = Boolean(hasSpecialPrice && isWithinDateRange);
    
    // Determine which price to use for commission calculation
    const applicablePrice = isSpecialPriceApplicable && product.specialPrice 
      ? Number(product.specialPrice) 
      : product.price;
    
    // Calculate commission values
    const percentCommissionValue = (applicablePrice * percentCommission) / 100;
    const totalCommission = fixCommission + percentCommissionValue;
    
    return {
      fixCommission,
      percentCommission,
      percentCommissionValue: parseFloat(percentCommissionValue.toFixed(2)),
      applicablePrice,
      totalCommission: parseFloat(totalCommission.toFixed(2)),
      isSpecialPriceApplicable
    };
  };

  // Handler for showing commission details in modal
  const handleShowCommissionDetails = (product: BestSellingProductType) => {
    setCurrentCommissionDetails(product);
    setShowCommissionModal(true);
  };

  // Handler for closing the commission modal
  const handleCloseCommissionModal = () => {
    setShowCommissionModal(false);
  };

  // Create table configuration
  const columns = useMemo(() => createColumns(t), [t]);
  const table = useAdvanceTable({
    data: tableData,
    columns,
    pageSize: 50,
    pagination: false, // Disable pagination
    sortable: true,
    initialState: {
      sorting: [
        {
          id: 'totalSold',
          desc: true // Sort in descending order (largest to smallest)
        }
      ]
    }
  });

  return (
    <BestSellingTableContext.Provider value={{ onShowCommissionDetails: handleShowCommissionDetails }}>
      {/* Commission Details Modal */}
      <CommissionDetailsModal
        show={showCommissionModal}
        onHide={handleCloseCommissionModal}
        product={currentCommissionDetails ? {
          id: currentCommissionDetails.id,
          name: currentCommissionDetails.name,
          sku: currentCommissionDetails.sku,
          price: currentCommissionDetails.price,
          status: currentCommissionDetails.status,
          product_status: currentCommissionDetails.productStatus,
          productImage: currentCommissionDetails.productImage,
          totalSold: currentCommissionDetails.totalSold,
          // Use the actual values from the product with proper type conversion
          dimensions: currentCommissionDetails.dimensions,
          weight: currentCommissionDetails.weight,
          shippingMethod: Number(currentCommissionDetails.shippingMethod || 1),
          specialPrice: currentCommissionDetails.specialPrice ? Number(currentCommissionDetails.specialPrice) : null,
          discountStartDate: currentCommissionDetails.discountStartDate,
          discountEndDate: currentCommissionDetails.discountEndDate
        } : null}
        calculateCommission={calculateTotalCommission}
      />
      <Row className="align-items-end justify-content-between pb-5 g-3">
        <Col xs="auto">
          <h3>{t("bestSellingProducts")}</h3>
          <p className="text-body-tertiary lh-sm mb-0">
            {t("topProductsBySales")}
          </p>
        </Col>
        <Col xs={12} md="auto">
          <Row className="g-2 gy-3">
            <Col xs="auto" className="d-flex gap-2">
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-warning"
                >
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  {timePeriodLabel}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleTimePeriodChange(null)}>{t("allTime")}</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTimePeriodChange('week')}>{t("thisWeek")}</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTimePeriodChange('month')}>{t("lastMonth")}</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleTimePeriodChange('three_months')}>{t("lastThreeMonths")}</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Link to="/products">
                <Button variant="outline-warning">
                  <FontAwesomeIcon icon={faList} className="me-2" />
                  {t("allProducts")}
                </Button>
              </Link>
              {/* Only show the Add Product button if we have data to display */}
              {tableData.length > 0 && (
                <Link to="/products/add">
                  <Button variant="warning">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {t("addProduct")}
                  </Button>
                </Link>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">
          <p>{t("errorLoadingProducts")}</p>
        </div>
      ) : tableData.length === 0 ? (
        <div className="text-center py-5">
          <div className="position-relative mb-4" style={{ height: '120px' }}>
            {/* Animated rocket with trail */}
            <div className="position-absolute" style={{ 
              left: '50%', 
              transform: 'translateX(-50%)',
              animation: 'rocketFly 3s infinite ease-in-out'
            }}>
              <div style={{ fontSize: '4rem' }}>🚀</div>
              {/* Rocket trail */}
              <div className="position-absolute" style={{ 
                top: '60px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                width: '20px',
                height: '60px',
                background: 'linear-gradient(to bottom, rgba(255,165,0,0.7), rgba(255,165,0,0))',
                borderRadius: '50%',
                animation: 'pulse 1.5s infinite',
                zIndex: -1
              }}></div>
            </div>
            
            {/* Stars */}
            <div className="position-absolute" style={{ 
              top: '30px', 
              left: '30%',
              fontSize: '1.5rem',
              animation: 'twinkle 2s infinite ease-in-out'
            }}>✨</div>
            <div className="position-absolute" style={{ 
              top: '10px', 
              right: '35%',
              fontSize: '1.2rem',
              animation: 'twinkle 3s infinite ease-in-out'
            }}>⭐</div>
            <div className="position-absolute" style={{ 
              bottom: '20px', 
              left: '40%',
              fontSize: '1rem',
              animation: 'twinkle 2.5s infinite ease-in-out'
            }}>✨</div>
          </div>
          
            <h4 className="fw-bold text-warning mb-2" style={{ animation: 'fadeIn 1s ease-in' }}>
              {t("bestSellsAwait")}
            </h4>
            <p className="fs-5 mb-1" style={{ animation: 'slideUp 1s ease-out' }}>
              {t("makesSalesMagic")}
            </p>
            <p className="text-body-tertiary" style={{ animation: 'slideUp 1.2s ease-out' }}>
              {t("productsSellInfo")}
          </p>
          
          <div className="mt-4" style={{ animation: 'bounce 2s infinite' }}>
            <Link to="/products/add">
              <Button variant="warning" className="px-4 shadow-sm" style={{ transition: 'all 0.3s' }}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t("addWinningProduct")}
              </Button>
            </Link>
          </div>
          
          {/* CSS Animations */}
          <style>
            {`
              @keyframes rocketFly {
                0% { transform: translateX(-50%) translateY(5px); }
                50% { transform: translateX(-50%) translateY(-5px); }
                100% { transform: translateX(-50%) translateY(5px); }
              }
              
              @keyframes pulse {
                0% { opacity: 0.7; height: 60px; }
                50% { opacity: 0.3; height: 40px; }
                100% { opacity: 0.7; height: 60px; }
              }
              
              @keyframes twinkle {
                0% { opacity: 0.3; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1.1); }
                100% { opacity: 0.3; transform: scale(0.8); }
              }
              
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              
              @keyframes slideUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
              
              @keyframes bounce {
                0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                40% { transform: translateY(-10px); }
                60% { transform: translateY(-5px); }
              }
            `}
          </style>
        </div>
      ) : (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table fs-9 mb-0 border-top border-translucent'
            }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          {/* Pagination footer removed */}
        </AdvanceTableProvider>
      )}
    </BestSellingTableContext.Provider>
  );
};

export default EcomBestSellingProductsTable;
